// shared/src/prompts/lht-thesis.ts
// Long-Horizon Thesis (LHT) NARRATION writer — the ONE LLM step in the LHT layer.
// docs/long-horizon-thesis-2026-07-09-brief-revision.md §3.5.
//
// Runs deepseek-v4-pro (low-frequency, high-stakes) ONLY on a committed stance
// change or the monthly review. The committed STANCE + the trend structure are
// GIVEN by the deterministic engine — the LLM does NOT choose direction. Its job:
// explain WHY the durable trend holds, decompose it into 2-4 named pillars each
// with a concrete FALSIFIER (≥1 a price level = the tripwire), set the
// invalidation/reclaim levels, and write the 12-month horizon answer.
//
// STATE-ONLY (no action verbs). No calibration SCALARS in the prompt (raw
// prices / trend structure ARE allowed; realized outcomes enter as concrete
// price EVENTS, never hit-rates — feedback_no_numeric_anchors_to_llm).

import { z } from 'zod';
import { llmNumber } from '../resolvers/llm-number-coerce.js';

export const VERSION    = '1.1.0';   // 2026-08-21 — SMA-anchor reframe: flip framed on the 200-DMA edge + phase, reversal band dropped (docs/lht-narration-sma-anchor-2026-08-21-design.md).
export const STEP       = 'lht.thesis';
export const MODEL      = 'deepseek-v4-pro';   // Pro: rare + high-stakes durable narrative. DESIGN-LOCKED to Pro (NOT a deviation — no MODEL_DEVIATION_ALLOWLIST entry); see _design-lock.ts DESIGN_MODEL_LOCK 'lht.thesis'.
// 2026-08-01 — raised 4000 → 8000. The 30-day headroom review found lht.thesis
// at 3284/4000 (82%). Output-only (non-reasoning Pro path: reasoning_tokens 0),
// so demand is more predictable than the M-stage reasoners, but the margin was
// thin for a verbose durable narrative. Ceiling, not target.
// docs/audits/2026-08-01-token-cap-headroom-review.md
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 8000)
export const ROLE       = 'analyst' as const;
export const DESC       = 'Narrates the durable 12-month trend thesis + falsifiers for a committed stance' as const;   // #45 registry — one-line operator description

const PillarSchema = z.object({
  id:          z.string(),                                  // short slug, e.g. 'trend-slope'
  claim:       z.string(),                                  // the load-bearing assumption
  falsifier:   z.string(),                                  // the concrete observable that would break it
  price_level: llmNumber(z.number().positive()).nullable().optional(),  // present when the falsifier is a price
});

export const OutputSchema = z.object({
  regime_label:  z.string(),                                // ≤4w label, e.g. 'secular uptrend'
  thesis_text:   z.string(),                                // 3-6 sentence durable narrative
  pillars:       z.array(PillarSchema).min(2).max(4),
  key_levels:    z.object({
    invalidation: llmNumber(z.number().positive()),         // price below/above which the stance breaks
    reclaim:      llmNumber(z.number().positive()),         // price that would re-confirm / upgrade
  }),
  horizon_answer: z.string(),                               // ≤40w 12-month state read for this scope
  confidence:     z.enum(['low', 'med', 'high']),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# LONG-HORIZON THESIS WRITER (durable trend narrator; STATE-ONLY)

## ROLE
A deterministic engine has ALREADY committed this ticker's long-term trend STANCE
(uptrend / downtrend / range) from months of price structure. You do NOT choose
the direction and you do NOT second-guess it. Your job is to NARRATE the durable
thesis behind that committed stance: explain why it holds, name what would break
it, and answer the 12-month outlook. You are writing the standing view a
buy-and-hold investor reads between rare changes — write it to still read true in
three months.

## ABSOLUTE: PREDICT STATE, NEVER PRESCRIBE ACTIONS
Never say buy / sell / trim / add / hold / exit / enter / rotate / cut / hedge /
scale / take-profit / stop. Describe the market's predicted STATE — trend,
levels, conditions, timing. The reader decides what to do.

## INPUTS (user message)
- SCOPE (ticker) + COMMITTED STANCE + SINCE (when the stance committed)
- TREND STRUCTURE: latest price, the long regression slope (annualized %), the
  trend t-strength (how statistically clean the trend is), and the 200-day average.
- SMA-ANCHORED FLIP: the 200-day average's SLOPE (rising/falling), the FLIP EDGE
  (the price whose SUSTAINED hold-beyond loses the trend = the 200-DMA plus/minus an
  exit buffer), and the current FLIP PHASE. The durable trend is LOST when price holds
  beyond the 200-DMA past that buffer for a dwell period — a sustained cross, NOT a
  single touch of any band. Phase: 'toward' = price has not reached the edge;
  'confirming' = price has ALREADY crossed the edge and a shift is being tested (dwell
  accruing); 'contested' = a breakout the trend strength does not yet confirm;
  'pending' = the SMA anchor is not yet computable.
- RECENT SHORT-TERM READ (optional): a digest of the fast daily signal tape — use
  ONLY to note whether the short-term view is confirming or diverging from the
  durable trend. Never let it flip your narrative; the stance is committed.
- REALIZED MOVES (optional): concrete past price outcomes (e.g. "60d ago read
  resolved -12%"). Cite as evidence; these are facts, not forecasts.
- PRIOR THESIS (optional): present on a CHANGE. Say what changed and why the old
  thesis broke — do not silently rewrite.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "regime_label":  "<=4 words, e.g. 'secular uptrend', 'range-bound', 'bear leg'>",
  "thesis_text":   "<3-6 sentences. Why this durable trend holds, grounded in the
                    trend structure (slope, price vs the 200d average, the flip edge +
                    phase). Name the single dominant driver in plain terms. If the phase
                    is 'confirming', SAY SO — price has cleared the anchor and a shift is
                    being tested. State-only.>",
  "pillars": [
    {
      "id":          "<short-slug>",
      "claim":       "<the load-bearing assumption, <=20w>",
      "falsifier":   "<the concrete observable that would break this pillar, <=20w>",
      "price_level": <number or null — REQUIRED (non-null) on at least ONE pillar:
                      the price whose sustained breach would break the trend>
    }
    // 2-4 pillars total
  ],
  "key_levels": {
    "invalidation": <the SMA-anchored FLIP EDGE — the price whose sustained hold-beyond
                     loses the trend. For 'toward' this is ahead of price on the breaking
                     side; for 'confirming' it is the edge price has ALREADY crossed>,
    "reclaim":      <the SMA reclaim level — price back to the trend side of the 200-DMA,
                     re-confirming the stance>
  },
  "horizon_answer": "<=40w — the 12-month STATE read for this ticker implied by the
                     durable trend + structure. Commit to a directional band, not a
                     hedge. State-only.>",
  "confidence": "low" | "med" | "high"
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON. No action verbs anywhere.
2. DO NOT contradict the committed stance. An 'uptrend' thesis is bullish-state;
   a 'downtrend' thesis is bearish-state; a 'range' thesis describes a bounded,
   directionless state. Your narrative MUST match the given stance.
3. At least ONE pillar carries a non-null price_level — that is the tripwire the
   investor watches. Set it to the SMA-anchored FLIP EDGE you were given (the price
   whose sustained hold-beyond loses the trend); do not invent an absurd level.
4. key_levels.invalidation is the FLIP EDGE. For flip_phase 'toward', that edge sits
   on the breaking side of price (below for an uptrend, above for a downtrend) — the
   level not yet reached. For flip_phase 'confirming', price has ALREADY crossed the
   edge, so invalidation IS that already-crossed edge (do NOT force it back to the far
   side of price — the stance is actively being invalidated). reclaim is the SMA
   reclaim level on the confirming side.
5. Ground the thesis in the STRUCTURE you were given (slope, 200d average, flip edge,
   phase). Do NOT invent fundamentals or news you were not given. If you have no driver
   beyond price structure, say the trend is technical/price-led — do not fabricate.
6. confidence reflects how clean the trend is (t-strength) + how far price sits from
   the FLIP EDGE and the dwell progress (gauge) — NOT your mood. A stance whose price
   already sits PAST the edge ('confirming', dwell accruing) is LOWER confidence than
   one riding well clear on the trend side.
7. horizon_answer commits to a 12-month directional band consistent with the
   stance. More time = wider band; anchor it on the structure, never a hedge.
8. PHASE-FIDELITY: the narrative MUST match flip_phase. 'confirming' means price has
   crossed the flip edge and a shift out of the committed stance is being tested (dwell
   accruing) — you MUST acknowledge this and may NOT write "firmly holding" / "price
   remains on the trend side"; describe the trend as under active test while the stance
   is not yet re-committed. 'toward' = the edge is the level the trend would be lost at,
   X% away. 'contested' = a level the trend must confirm, not a promised flip. 'pending'
   = the SMA anchor isn't computable yet (say so). You still do NOT change the committed
   stance (rule 2) — you narrate its current pressure honestly.
`;

export interface LhtThesisInput {
  readonly scope:         string;
  readonly stance:        'uptrend' | 'downtrend' | 'range';
  readonly since_date:    string;
  readonly price:         number;
  readonly slope_ann_pct: number;
  readonly t_stat:        number;
  readonly sma_long:      number | null;
  // SMA-ANCHORED FLIP (2026-08-21 reframe) — the trigger the narrative must match. The
  // reversal band was DROPPED (the engine does not flip on it; feeding it made the model
  // anchor on the retired concept). flip_edge/flip_phase come from the shared lhtFlip.
  readonly sma_slope_pct: number | null;  // % slope of the 200-DMA (rising>0 / falling<0); null pre-SMA
  readonly flip_edge:     number | null;  // sma × (1 ± exit buffer) — the price whose sustained hold-beyond loses the trend
  readonly flip_phase:    'toward' | 'confirming' | 'contested' | 'pending';
  readonly gauge:         number;         // 0–100 dwell pressure (for the confirming copy)
  readonly recent_short_term?: string;    // pre-digested fast-tape summary
  readonly realized_moves?:    ReadonlyArray<string>;
  readonly prior_thesis?:      string;    // present on a CHANGE (never on a monthly re-review)
  readonly review_kind?:       'monthly'; // present on the scheduled monthly re-review (S-17)
}

function n(v: number): string { return (Math.round(v * 100) / 100).toString(); }

/** One-line phase read for the prompt — tells the model the CURRENT flip state so the
 *  narrative reflects it (esp. confirming, which the model MUST acknowledge). */
function flipPhaseLine(input: LhtThesisInput): string {
  const movePct = (input.flip_edge != null && input.price !== 0)
    ? Math.abs(((input.flip_edge - input.price) / input.price) * 100).toFixed(1)
    : null;
  switch (input.flip_phase) {
    case 'toward':
      return `toward — price has NOT reached the flip edge${movePct ? ` (~${movePct}% away)` : ''}; the trend would be lost there`;
    case 'confirming':
      return `confirming — price has ALREADY crossed the flip edge; a shift is being TESTED, dwell accruing (gauge ${input.gauge}/100). The narrative MUST acknowledge this — do not claim the stance is unchallenged`;
    case 'contested':
      return `contested — a breakout the trend strength does not yet confirm (a challenge level, NOT a promised flip)`;
    case 'pending':
      return `pending — the SMA anchor is not yet computable (early history)`;
  }
}

export function buildUserPrompt(input: LhtThesisInput): string {
  const lines: string[] = [
    `SCOPE: ${input.scope}`,
    `COMMITTED STANCE: ${input.stance}   (committed since ${input.since_date})`,
    '',
    'TREND STRUCTURE:',
    `  latest_price:        ${n(input.price)}`,
    `  regression_slope:    ${n(input.slope_ann_pct)}% annualized`,
    `  trend_t_strength:    ${n(input.t_stat)}   (|value| >= 2.5 = a clean, well-established trend)`,
    `  200d_average:        ${input.sma_long == null ? 'n/a (insufficient history)' : n(input.sma_long)}`,
    '',
    'SMA-ANCHORED FLIP (the durable trend is price vs a rising/falling 200-DMA; it is LOST on a',
    'SUSTAINED cross of the 200-DMA past the exit buffer + a dwell — NOT on a band touch):',
    `  200d_slope:          ${input.sma_slope_pct == null ? 'n/a' : `${n(input.sma_slope_pct)}%  (${input.sma_slope_pct > 0 ? 'rising' : input.sma_slope_pct < 0 ? 'falling' : 'flat'})`}`,
    `  flip_edge:           ${input.flip_edge == null ? 'n/a (SMA anchor pending)' : n(input.flip_edge)}   (price crossing + holding beyond this loses the trend)`,
    `  flip_phase:          ${flipPhaseLine(input)}`,
  ];
  lines.push('');
  lines.push('RECENT SHORT-TERM READ (fast tape — confirm/diverge note only, never flips the stance):');
  lines.push(`  ${input.recent_short_term && input.recent_short_term.length > 0 ? input.recent_short_term : '(none supplied)'}`);
  lines.push('');
  lines.push('REALIZED MOVES (facts, not forecasts):');
  if (input.realized_moves && input.realized_moves.length > 0) {
    for (const m of input.realized_moves) lines.push(`  - ${m}`);
  } else {
    lines.push('  (none supplied)');
  }
  if (input.prior_thesis && input.prior_thesis.length > 0) {
    lines.push('');
    lines.push('PRIOR THESIS (the stance just CHANGED — say what broke):');
    lines.push(`  ${input.prior_thesis}`);
  }
  // S-17 monthly no-incumbent-bias re-review: the standing thesis is re-derived on a
  // ~monthly clock even when the stance has NOT changed, to defeat incumbency (a stale
  // narrative silently persisting). The prior narrative is deliberately WITHHELD (never
  // passed as prior_thesis) so the model re-derives fresh from current structure.
  if (input.review_kind === 'monthly') {
    lines.push('');
    lines.push('REVIEW MODE — SCHEDULED MONTHLY RE-REVIEW (no incumbency):');
    lines.push('  This is the periodic re-derivation of the standing thesis; the stance itself has NOT');
    lines.push('  changed. Last month\'s narrative is deliberately WITHHELD. Re-derive the thesis FRESH');
    lines.push('  from the CURRENT trend structure above, as if seeing this ticker for the first time —');
    lines.push('  do not assume a prior view. If the structure still supports the committed stance,');
    lines.push('  restate the thesis in current terms; where the structure has drifted (slope, price vs');
    lines.push('  the 200d average, the flip edge / phase), say so plainly in the narrative and pillars.');
  }
  lines.push('');
  lines.push('REQUEST: emit_lht_thesis');
  lines.push('');
  lines.push('Emit a SINGLE JSON object matching the schema. Narrate the committed stance — do NOT change it. STATE only, no action verbs. At least one pillar carries a non-null price_level (the tripwire).');
  return lines.join('\n');
}
